import { DISCORD } from "constants/links";

import React, { useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Typography,
  DialogTitle,
  DialogContent,
  Link,
  AccordionSummary,
  Divider,
  useTheme,
  useMediaQuery,
  Stack,
  Skeleton,
  Alert,
  AlertTitle,
  Button,
} from "@mui/material";
import { postCashbackSatsUtilizations } from "API/calls";
import { Credit } from "components/icons";
import { fontWeight } from "config/Theme/options";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useAPICashbackSatsUtilizationsPreview } from "hooks/useAPI";
import { useSnackbar } from "notistack";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CashbackSatsUtilizationType } from "types/enums";
import { getColorByMode } from "utils";
import { renderNumber, renderPrice } from "utils/formatter";

import { StyledAccordion, StyledDialog, StyledIconButton } from "./styles";
import IProps, { UTILIZATION_TYPES, UtilizationItemProps, StatItemProps } from "./types";

const StatItem: React.FC<StatItemProps> = ({ label, value, icon }) => {
  const { colors } = useMUIThemeModeContext();

  return (
    <Stack>
      <Typography color={colors.gray500} fontWeight={fontWeight.regular} fontSize={{ xs: "0.625rem", sm: "0.75rem" }}>
        {label}
      </Typography>
      {value ? (
        <Stack spacing={0.5} direction="row" alignItems="center">
          <Box>
            <Typography color={colors.black} fontWeight={fontWeight.medium} fontSize={{ xs: "1.25rem", sm: "1.5rem" }}>
              {value}
            </Typography>
          </Box>
          {icon && <Box>{icon}</Box>}
        </Stack>
      ) : (
        <Skeleton variant="text" width={100} height={32} />
      )}
    </Stack>
  );
};

const Item = ({ value, type, onClick, data, hasAvailableSats, onSubmit, isSubmitting }: UtilizationItemProps) => {
  const { t } = useTranslation();
  const { colors } = useMUIThemeModeContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChange = (panel: typeof UTILIZATION_TYPES[number]) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    onClick(isExpanded ? panel : undefined);
  };

  const getValues = () => {
    if (type === "LIGHTNING") return null;

    if (!hasAvailableSats) {
      return (
        <Box mt={2}>
          <Alert severity="warning">
            <AlertTitle>
              <Trans i18nKey="cashbackSatsList.utilizationModal.notEnoughSats.title" />
            </AlertTitle>
            <Trans i18nKey="cashbackSatsList.utilizationModal.notEnoughSats.description" />
          </Alert>
        </Box>
      );
    }

    const statItems: StatItemProps[] = [
      {
        label: t(`cashbackSatsList.utilizationModal.${type}.btcPrice`),
        value: data ? renderPrice(data.btcCzkRate) : undefined,
      },
      {
        label: t(`cashbackSatsList.utilizationModal.${type}.czkValue`),
        value: data ? renderPrice(data.czkValue, undefined, 2, true) : undefined,
      },
    ];

    if (type === CashbackSatsUtilizationType.CONVERSION_TO_CREDITS) {
      statItems.push({
        label: t(`cashbackSatsList.utilizationModal.${type}.conversionCredits`),
        value: data ? `${renderNumber(data.conversionCredits)}` : undefined,
        icon: <Credit sx={{ fontSize: 24 }} />,
      });
    }

    return (
      <Stack spacing={{ xs: 1, sm: 4 }} direction={{ xs: "column", sm: "row" }} mt={2}>
        {statItems.map((item, idx) => (
          <React.Fragment key={idx}>
            <StatItem label={item.label} value={item.value} icon={item.icon} />
            {idx < statItems.length - 1 && (
              <Divider orientation={isMobile ? "horizontal" : "vertical"} flexItem />
            )}
          </React.Fragment>
        ))}
      </Stack>
    );
  };

  const getCta = () => {
    if (type === "LIGHTNING") return null;

    let value: string | undefined;
    if (type === CashbackSatsUtilizationType.DONATION_TO_STOSUJ) {
      value = data ? renderPrice(data.czkValue, undefined, 2, true) : undefined;
    } else if (type === CashbackSatsUtilizationType.CONVERSION_TO_CREDITS) {
      value = data ? renderNumber(data.conversionCredits) : undefined;
    }

    return (
      <Box mt={3}>
        <Button
          disabled={!hasAvailableSats || !data || isSubmitting}
          variant="contained"
          onClick={onSubmit}
          fullWidth={isMobile}
        >
          {t(`cashbackSatsList.utilizationModal.${type}.cta${(!hasAvailableSats || !data || isSubmitting) ? 'Disabled' : ''}`, { value })}
        </Button>
      </Box>
    );
  };

  return (
    <StyledAccordion expanded={value === type} onChange={handleChange(type)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
        <Typography fontSize="0.875rem" fontWeight={fontWeight.medium}>
          {t(`cashbackSatsList.utilizationModal.${type}.title`)}
        </Typography>
      </AccordionSummary>
      <Box pt={2}>
        <Typography color={colors.blueGray} fontWeight={fontWeight.light} fontSize={{ xs: "0.75rem", sm: "0.875rem" }}>
          <Trans
            i18nKey={`cashbackSatsList.utilizationModal.${type}.description`}
            components={{ a: <Link href={DISCORD} target="_blank" color="inherit" /> }}
          />
        </Typography>
        {getValues()}
        {getCta()}
      </Box>
    </StyledAccordion>
  );
};

const UtilizationsModal: React.FC<IProps> = ({ available, onClose, refresh }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { colors } = useMUIThemeModeContext();
  const navigate = useNavigate();
  const theme = useTheme();

  const hasAvailableSats = available >= 100;
  const [selectedUtilization, setSelectedUtilization] = useState<typeof UTILIZATION_TYPES[number]>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data, error } = useAPICashbackSatsUtilizationsPreview(hasAvailableSats && Boolean(selectedUtilization));

  useEffect(() => {
    if (error) {
      onClose();
      enqueueSnackbar(t("commonError"), { variant: "error" });
    }
  }, [error]);

  const handleUtilization = async () => {
    if (!selectedUtilization) return;
    setIsSubmitting(true);
    try {
      await postCashbackSatsUtilizations(selectedUtilization as CashbackSatsUtilizationType);
      refresh();
      enqueueSnackbar(t("cashbackSatsList.utilizationModal.success", { value: renderNumber(available) }), { variant: "success" });
      if (selectedUtilization === CashbackSatsUtilizationType.CONVERSION_TO_CREDITS) {
        navigate("/stosaky/transakce");
      }
      onClose();
    } catch (error: any) {
      enqueueSnackbar(t("commonError"), { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <StyledDialog open={true} onClose={onClose} fullWidth scroll="paper">
      <StyledIconButton color="inherit" onClick={onClose} size="small">
        <CloseIcon />
      </StyledIconButton>
      <DialogTitle>{t("cashbackSatsList.utilizationModal.title")}</DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, md: 4 }} pb={{ xs: 2, md: 3 }}>
          <Stack>
            <Typography
              color={getColorByMode(theme, colors.gray500, colors.gray600)}
              fontWeight={fontWeight.regular}
              fontSize={{ xs: "0.625rem", sm: "0.75rem" }}>
              {t("cashbackSatsList.utilizationModal.available")}
            </Typography>
            <Typography
              color={colors.success}
              fontWeight={fontWeight.medium}
              fontSize={{ xs: "1.25rem", sm: "1.5rem" }}>
              {renderNumber(available)}&nbsp;{t(`cashbackSatsList.sats`, { count: available })}
            </Typography>
          </Stack>
          <Typography
            color={colors.blueGray}
            fontWeight={fontWeight.light}
            fontSize={{ xs: "0.75rem", sm: "0.875rem" }}>
            <Trans
              i18nKey="cashbackSatsList.utilizationModal.description"
              components={{ a: <Link href={DISCORD} target="_blank" color="inherit" /> }}
            />
          </Typography>
        </Stack>
        <Stack spacing={1.5} pb={3}>
          {UTILIZATION_TYPES.map((type) => (
            <Item
              key={type}
              type={type}
              value={selectedUtilization}
              onClick={setSelectedUtilization}
              data={data}
              hasAvailableSats={hasAvailableSats}
              onSubmit={handleUtilization}
              isSubmitting={isSubmitting}
            />
          ))}
        </Stack>
      </DialogContent>
    </StyledDialog>
  );
};

export default UtilizationsModal;
